/**
 * A version of useMemo that makes semantic guarantees about the memoized
 * value not being dropped/recreated by react. Useful for lazily initializing
 * an expensive value that doesn't require teardown.
 *
 * Mostly taken from https://github.com/alexreardon/use-memo-one
 */
import { useRef, useEffect } from "react";

type DependencyList = ReadonlyArray<unknown>;

interface Cache<T> {
  deps: DependencyList;
  result: T;
}

export function useMemoOne<T>(getResult: () => T, deps: DependencyList): T {
  const committed = useRef<Cache<T> | null>(null);

  // create a new cache if required
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cache: Cache<T> =
    committed.current && areDepsEqual(deps, committed.current.deps)
      ? committed.current
      : {
          deps,
          result: getResult(),
        };

  // commit the cache
  useEffect(() => {
    committed.current = cache;
  }, [cache]);

  return cache.result;
}

function areDepsEqual(nextDeps: DependencyList, prevDeps: DependencyList) {
  // no checks needed if the deps length has changed
  if (nextDeps.length !== prevDeps.length) {
    return false;
  }
  // Using for loop for speed. It generally performs better than array.every
  // https://github.com/alexreardon/memoize-one/pull/59

  for (let i = 0; i < nextDeps.length; i++) {
    // using shallow equality check
    if (nextDeps[i] !== prevDeps[i]) {
      return false;
    }
  }
  return true;
}
